import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import DesktopHeaderOptions from './HeaderOptions';
import LogoIcon from '@/components/component-library/Icon/types/Logo';
import HeaderBar from '@/components/app/Headers/HeaderBar';
import LayoutWrapperHorizontal from '@/components/component-library/LayoutWrapperHorizontal';
import { GAclosedGenresDropdown, GAopenedGenresDropdown } from '@/helpers/events-ga/navigation';
import { classes } from '@/helpers/styling';
import useActiveRoutes from '@/hooks/routing/useActiveRoutes';
import useBrowserPopState from '@/hooks/browser/useBrowserPopState';
import useGA from '@/hooks/ga/useGA';
import pageKeys from '@/helpers/routing/constants';
import Link from '@/components/component-library/Link';
import Span from '@/components/component-library/Span';
import Gradient, { GRADIENT_POSITION_TOP } from '@/components/app/Gradient';

const GenresOverlay = dynamic(import('./GenresOverlay'), { ssr: false });

// Desktop header bar with Keakie logo and header options
const DesktopHeaderBar = ({ className }) => {
  const { sendGAEvent } = useGA();

  const activeRoutesObject = useActiveRoutes();

  // Flag for if the genres overlay is currently open
  const [genresOverlayOpen, setGenresOverlayOpen] = useState(false);

  // If the route changes, close the genre overlay
  const handlePopState = useCallback(() => {
    setGenresOverlayOpen(false);
  }, []);
  
  useBrowserPopState(handlePopState);

  // ------------------- CLICK HANDLERS -------------------
  const handleCloseGenreOverlay = useCallback(() => {
    setGenresOverlayOpen(false);
  }, []);

  const handleClickGenres = useCallback(() => {
    const isOpen = !genresOverlayOpen;

    setGenresOverlayOpen(isOpen);

    if (isOpen) {
      sendGAEvent(GAopenedGenresDropdown());
    } else {
      sendGAEvent(GAclosedGenresDropdown());
    }
  }, [sendGAEvent, genresOverlayOpen]);

  const handleClickGenre = useCallback(() => {
    handleCloseGenreOverlay();
  }, [handleCloseGenreOverlay]);

  const handleClickMoreGenres = useCallback(() => {
    handleCloseGenreOverlay();
  }, [handleCloseGenreOverlay]);

  const handleClickSearch = useCallback(() => {
    handleCloseGenreOverlay();
  }, [handleCloseGenreOverlay]);

  const handleClickOptions = useCallback(() => {
    handleCloseGenreOverlay();
  }, [handleCloseGenreOverlay]);

  return (
    <>
      <HeaderBar
        className={ classes(
          { '--overlay-open': genresOverlayOpen },
          'header-desktop',
          'dskt__header-bar',
          className
        ) }
      >
        <Gradient breadth='200%' gradientBreadth='100%' position={ GRADIENT_POSITION_TOP } />
        <LayoutWrapperHorizontal>
          <div className='header-desktop-content'>
            <div className='left'>
              <Link href={ pageKeys.discover } label='Keakie logo'>
                <Span
                  className={ classes('logo-icon-wrapper', { '--is-active': activeRoutesObject.discoverActive }) }
                  onClick={ handleCloseGenreOverlay }
                >
                  <LogoIcon />
                </Span>
              </Link>
            </div>
            <div className='right'>
              <DesktopHeaderOptions
                genresOverlayOpen={ genresOverlayOpen }
                onClickDiscover={ handleCloseGenreOverlay }
                onClickGenres={ handleClickGenres }
                onClickLibrary={ handleCloseGenreOverlay }
                onClickLogin={ handleCloseGenreOverlay }
                onClickOptions={ handleClickOptions }
                onClickProfile={ handleCloseGenreOverlay }
                onClickSearch={ handleClickSearch }
              />
            </div>
          </div>
        </LayoutWrapperHorizontal>
      </HeaderBar>
      <GenresOverlay
        onClickDiscoverMore={ handleClickMoreGenres }
        onClickGenre={ handleClickGenre }
        open={ genresOverlayOpen }
      />
    </>
  );
};

DesktopHeaderBar.propTypes = { className: PropTypes.string };

DesktopHeaderBar.defaultProps = { className: '' };

export default DesktopHeaderBar;